import { useLayoutEffect, type RefObject } from 'react'

/**
 * Fades the edges of a sideways-scrolling row (pair with `.gd-scroll-x`) only where more content
 * hides, so a cut-off chip reads as "swipe for more" rather than a clipped layout. Sets
 * `--fade-l` / `--fade-r` on the element; the mask in globals.css reads them.
 */
export function useScrollFade(ref: RefObject<HTMLElement | null>, size = 20): void {
  useLayoutEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const update = () => {
      const max = el.scrollWidth - el.clientWidth
      const left = Math.min(el.scrollLeft, size)
      const right = Math.min(Math.max(max - el.scrollLeft, 0), size)
      el.style.setProperty('--fade-l', `${max > 1 ? left : 0}px`)
      el.style.setProperty('--fade-r', `${max > 1 ? right : 0}px`)
    }
    update()
    el.addEventListener('scroll', update, { passive: true })
    const observer = new ResizeObserver(update)
    observer.observe(el)
    for (const child of el.children) observer.observe(child)
    return () => {
      el.removeEventListener('scroll', update)
      observer.disconnect()
    }
  }, [ref, size])
}
